import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { isNativeApp, nativeShare, triggerHaptic, openExternalUrl } from '../utils/nativeBridge';

// Share a post (or anything with a link): send it to someone you've been
// chatting with in one tap, copy the link, or hand it to another app.

const MAX_RECENT = 12;

// The system share sheet ("More"): the apps, or browsers with the Web Share API
const canShareToApps = () => isNativeApp() || typeof navigator.share === 'function';

// Recent chats, kept for the session so the sheet opens with them already there
let recentCache = null;

const loadRecent = async () => {
  const { data } = await axios.get('/api/messages/conversations');
  recentCache = (data || []).slice(0, MAX_RECENT).map((c) => ({
    id: c.userId,
    username: c.username || '',
    handle: (c.handle || '').replace(/^@+/, ''),
    profilePicture: c.profilePicture || null,
  }));
  return recentCache;
};

const Avatar = ({ person }) => {
  const [failed, setFailed] = useState(false);
  if (person.profilePicture && !failed) {
    return (
      <img
        src={getMediaUrl(person.profilePicture)}
        alt=""
        onError={() => setFailed(true)}
        className="w-14 h-14 rounded-full object-cover bg-zinc-200 dark:bg-zinc-800"
      />
    );
  }
  return (
    <span className="w-14 h-14 rounded-full flex items-center justify-center bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 text-lg font-semibold">
      {(person.username || person.handle || '?').charAt(0).toUpperCase()}
    </span>
  );
};

const Action = ({ icon, label, onClick, className = '' }) => (
  <button type="button" onClick={onClick} className="w-[72px] shrink-0 flex flex-col items-center gap-1.5 touch-press">
    <span className={`w-14 h-14 rounded-full flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-xl ${className}`}>
      <i className={icon} />
    </span>
    <span className="text-[12px] text-zinc-600 dark:text-zinc-300 text-center leading-tight">{label}</span>
  </button>
);

/**
 * @param {{ open: boolean, onClose: () => void, url: string, text?: string, title?: string }} props
 */
const ShareSheet = ({ open, onClose, url, text = '', title = 'Bazzef' }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const [recent, setRecent] = useState(recentCache || []);
  // userId -> 'sending' | 'sent'
  const [sent, setSent] = useState({});

  useEffect(() => {
    if (!open || !user) return;
    setSent({});
    loadRecent().then(setRecent).catch((err) => console.warn('Share: recent chats unavailable', err));
  }, [open, user]);

  if (!open) return null;

  const close = (e) => {
    e?.stopPropagation();
    onClose();
  };

  const sendTo = async (person) => {
    if (sent[person.id]) return;
    triggerHaptic('light');
    setSent((s) => ({ ...s, [person.id]: 'sending' }));
    try {
      const clientId = `share-${Date.now()}-${person.id}`;
      const { data } = await axios.post(`/api/messages/${person.id}`, { content: url, clientId });
      if (data?.limitReached && !data.id) throw new Error('limit');
      setSent((s) => ({ ...s, [person.id]: 'sent' }));
    } catch (err) {
      setSent((s) => ({ ...s, [person.id]: undefined }));
      toast.error(err.response?.data?.error || t('share.sendFailed', "Couldn't send. Try again."));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('share.linkCopied', 'Link copied'));
      onClose();
    } catch {
      toast.error(t('share.copyFailed', "Couldn't copy the link"));
    }
  };

  const openExternal = (href) => {
    openExternalUrl(href);
    onClose();
  };

  const moreApps = async () => {
    onClose();
    await nativeShare({ title, text, url, dialogTitle: t('share.title', 'Share') });
  };

  const message = text ? `${text} ${url}` : url;

  // On body, so a scrolling or transformed parent (the Lamha feed) can't clip it
  return createPortal(
    <div
      className="share-sheet fixed inset-0 z-50 flex items-end justify-center bg-black/50"
      onClick={close}
      // React events still bubble out of a portal: keep taps and swipes here
      // from reaching the post (opens it) or the Lamha feed (swipes it)
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-lg bg-white dark:bg-zinc-950 rounded-t-3xl pt-2 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-2xl animate-in slide-in-from-bottom duration-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={t('share.title', 'Share')}
      >
        <div className="w-10 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full mx-auto mb-3" />

        {user && recent.length > 0 && (
          <>
            <p className="px-5 pb-2 text-[13px] font-semibold text-zinc-500 dark:text-zinc-400">
              {t('share.sendTo', 'Send to')}
            </p>
            <div className="flex gap-1 overflow-x-auto no-scrollbar px-3 pb-4">
              {recent.map((person) => {
                const state = sent[person.id];
                return (
                  <button
                    key={person.id}
                    type="button"
                    onClick={() => sendTo(person)}
                    disabled={!!state}
                    className="w-[72px] shrink-0 flex flex-col items-center gap-1.5 touch-press"
                  >
                    <span className="relative">
                      <Avatar person={person} />
                      {state && (
                        <span className="absolute inset-0 rounded-full bg-black/45 flex items-center justify-center text-white">
                          <i className={`fas ${state === 'sent' ? 'fa-check' : 'fa-spinner fa-spin'} text-lg`} />
                        </span>
                      )}
                    </span>
                    <span className="w-full text-[12px] text-zinc-700 dark:text-zinc-300 text-center truncate">
                      {state === 'sent' ? t('share.sent', 'Sent') : person.username || `@${person.handle}`}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mx-5 mb-4 border-t border-zinc-100 dark:border-zinc-800" />
          </>
        )}

        <div className="flex gap-1 overflow-x-auto no-scrollbar px-3 pb-3">
          <Action icon="fas fa-link" label={t('share.copyLink', 'Copy link')} onClick={copyLink} className="text-zinc-700 dark:text-zinc-200" />
          <Action
            icon="fab fa-whatsapp"
            label="WhatsApp"
            onClick={() => openExternal(`https://wa.me/?text=${encodeURIComponent(message)}`)}
            className="text-emerald-500"
          />
          <Action
            icon="fab fa-x-twitter"
            label="X"
            onClick={() => openExternal(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`)}
            className="text-zinc-900 dark:text-white"
          />
          <Action
            icon="fab fa-facebook"
            label="Facebook"
            onClick={() => openExternal(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)}
            className="text-[#1877F2]"
          />
          {canShareToApps() && (
            <Action icon="fas fa-ellipsis" label={t('share.more', 'More')} onClick={moreApps} className="text-zinc-700 dark:text-zinc-200" />
          )}
        </div>

        <div className="px-4">
          <button
            type="button"
            onClick={close}
            className="w-full py-3.5 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 text-zinc-900 dark:text-zinc-100 font-bold text-[15px] touch-press"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareSheet;
